import * as React from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Stack,
	Switch,
	TextField,
} from "@mui/material";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import { RateCardItem, useUpdateRateCardItem } from "@/hooks/api/use-rate-card";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

/** Edit one line of the rate card: its price, wording, and whether clients can pick it. */
export function EditItemDialog({ item, onClose }: { item: RateCardItem | null; onClose: () => void }) {
	const update = useUpdateRateCardItem();
	const [values, setValues] = React.useState({
		name: "",
		description: "",
		amount: "",
		unit: "",
		priceNote: "",
		optional: false,
	});
	const [error, setError] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!item) return;
		setValues({
			name: item.name,
			description: item.description ?? "",
			amount: item.amount === null ? "" : String(Number(item.amount)),
			unit: item.unit ?? "",
			priceNote: item.priceNote ?? "",
			optional: item.optional,
		});
		setError(null);
	}, [item]);

	const set = (key: keyof typeof values, value: string | boolean) => {
		setValues((current) => ({ ...current, [key]: value }));
		setError(null);
	};

	const save = () => {
		if (!item) return;
		const amount = values.amount.trim() === "" ? null : Number(values.amount);
		if (!values.name.trim()) return setError("Give the line a name");
		if (amount !== null && (Number.isNaN(amount) || amount < 0)) return setError("Give the price as a number");
		if (amount === null && !values.priceNote.trim()) return setError("Give a price, or a note to show instead");

		update.mutate(
			{
				id: item.id,
				data: {
					name: values.name.trim(),
					description: values.description.trim() || null,
					amount,
					unit: values.unit.trim() || null,
					priceNote: values.priceNote.trim() || null,
					optional: values.optional,
				},
			},
			{ onSuccess: onClose }
		);
	};

	return (
		<Dialog
			open={Boolean(item)}
			onClose={onClose}
			sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "560px" } } }}
		>
			<DialogTitle>
				Edit rate card line
				<IconButton color="inherit" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<XIcon width={20} height={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ pt: "8px !important" }}>
				<Stack spacing={2}>
					<TextField label="Name" value={values.name} onChange={(event) => set("name", event.target.value)} />
					<TextField
						label="Description"
						multiline
						minRows={3}
						value={values.description}
						onChange={(event) => set("description", event.target.value)}
					/>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<TextField
							label="Price (excl. GST)"
							value={values.amount}
							onChange={(event) => set("amount", event.target.value)}
							inputMode="decimal"
							slotProps={{
								input: { startAdornment: <InputAdornment position="start">{item?.currency ?? "SGD"}</InputAdornment> },
							}}
							helperText="Leave empty to show the note instead"
							fullWidth
						/>
						<TextField
							label="Unit"
							value={values.unit}
							onChange={(event) => set("unit", event.target.value)}
							placeholder="per campaign"
							fullWidth
						/>
					</Stack>
					<TextField
						label="Note instead of a price"
						value={values.priceNote}
						onChange={(event) => set("priceNote", event.target.value)}
						placeholder="At cost, by market"
					/>
					{/* Setup lines price every request and usage is billed per minute: neither is a client's pick. */}
					{item?.section === "setup" || item?.section === "usage" ? null : (
						<FormControlLabel
							control={<Switch checked={values.optional} onChange={(event) => set("optional", event.target.checked)} />}
							label="Clients can tick this as an optional service on the request form"
						/>
					)}
					{error ? <CustomFormHelperError text={error} /> : null}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 3 }}>
				<Button color="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button variant="contained" onClick={save} disabled={update.isPending}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
